
import * as TaskExecutors from './task/executors';
import * as TaskHandler from './task/handler';

export default class Task extends Promise
{
    constructor(typeOrExecutor, data = {}, executor = undefined) {
        if(typeof typeOrExecutor == 'function') {
            super(typeOrExecutor);
        } else {
            let executionHandler = {
                resolve: null,
                reject: null
            };

            super((resolve, reject) => {
                executionHandler.resolve = resolve;
                executionHandler.reject = reject;
            });

            this.type = typeOrExecutor;
            this.taskData = data;
            this.executor = executor ? executor : this.getExecutor();
            this.executionHandler = executionHandler;
        }
        
        this.id = Math.random().toString(36).substr(2, 9);
        this.taskName = null;
        this.isDeferred = false;

        this.handler = {
            resolved: [],
            rejected: [],
            finally: [],
            deferred: []
        };
    }

    name(name = undefined) {
        if(name === undefined) {
            return this.taskName;
        } else {
            this.taskName = name;

            return this;
        }
    }
    
    data(newData = undefined, merge = true) {
        if(newData === undefined) {
            return this.taskData;
        } else {
            if(merge && newData && typeof newData == 'object' && this.taskData && typeof this.taskData == 'object') {
                this.taskData = Object.assign(this.taskData, newData);
            } else {
                this.taskData = newData;
            }

            return this;
        }
    }

    run() {
        if(!this.isDeferred) {
            this.executor(this.executionHandler.resolve, this.executionHandler.reject);
        }

        return this;
    }

    defer() {
        this.isDeferred = true;

        let result = this;

        if(this.handler.deferred.length) {
            this.handler.deferred.forEach(deferredHandler => {
                result = result.deferred(deferredHandler);
            })
        }

        return result;
    }

    deferred(onDeffered) {
        if(this.isDeferred) {
            try {
                const result = this.getHandler(onDeffered)();
                this.executionHandler.resolve(result);
            } catch(error) {
                this.executionHandler.reject(error);
            }
        } else {
            this.handler.deferred.push(onDeffered);
        }

        return this;
    }

    then(onResolved, onRejected = undefined, always = false) {
        onResolved = this.parseHandler(onResolved, 'resolved');
        onRejected = this.parseHandler(onRejected, 'rejected');

        let ret = this;

        if(always || !this.isDeferred) {
            ret = super.then(onResolved, onRejected);
            this.assignProperties(ret);
        }

        return ret;
    }

    thenDeferred(onResolved, onRejected = undefined) {
        this.parseHandler(onResolved, 'resolved', true);
        this.parseHandler(onRejected, 'rejected', true);

        return this;
    }

    thenAlways(onResolved, onRejected = undefined) {
        return this.then(onResolved, onRejected, true);
    }

    catch(onRejected, always = false) {
        onRejected = this.parseHandler(onRejected, 'rejected');

        let ret = this;

        if(always || !this.isDeferred) {
            ret = super.catch(onRejected);
            this.assignProperties(ret);
        }

        return ret;
    }

    catchDeferred(onRejected) {
        this.parseHandler(onRejected, 'rejected', true);

        return this;
    }

    catchAlways(onRejected) {
        return this.catch(onRejected, true);
    }

    finally(finallyHandler, always = false) {
        finallyHandler = this.parseHandler(finallyHandler, 'finally');

        let ret = this;

        if(always || !this.isDeferred) {
            ret = super.finally(finallyHandler);
            this.assignProperties(ret);
        }

        return ret;
    }

    finallyAlways(finallyHandler) {
        return this.finally(finallyHandler, always);
    }

    finallyDeferred(finallyHandler) {
        this.parseHandler(finallyHandler, 'finally', true);

        return this;
    }

    assignProperties(target) {
        target.id = this.id;
        target.taskName = this.taskName;
        target.taskData = this.taskData;
        target.handler = this.handler;
        target.isDeferred = this.isDeferred;
    }

    parseHandler(handler, deferredAddType = null, isDeferredHandler = false) {
        if(handler) {
            if(typeof handler == 'string') {
                handler = handler.split('::');
            }
            if(deferredAddType) {
                if(Array.isArray(handler)) {
                    this.handler[deferredAddType].push(handler);
                } else if(isDeferredHandler) {
                    throw new Error('Invalid deferred task handler, only strings and arrays are allowed');
                }
            }
        }

        return this.getHandler(handler);
    }

    getHandler(handler) {
        if(handler == undefined || typeof handler == 'function') {
            return handler;
        }
        if(typeof handler != 'string' && !Array.isArray(handler)) {
            throw new Error('Invalid task handler type');
        }

        const handlerParts = typeof handler == 'string' ? handler.split('::') : handler;
        const type = handlerParts[0];
        const command = handlerParts.length > 1 ? handlerParts[1] : null;
        const args = handlerParts.length > 2 ? handlerParts[2] : undefined;

        if(type in TaskHandler) {
            if(command) {
                return result => { return TaskHandler[type](command, result, args); };
            } else {
                return result => { return TaskHandler[type](result, args); };
            }
        } else {
            throw new Error(`Cannot find task handler for ${handler}`);
        }
    }

    getExecutor() {
        if(this.type in TaskExecutors) {
            return TaskExecutors[this.type](this.taskData);
        } else {
            throw new Error(`Cannot find task executor for task type ${this.type}`);
        }
    }
}